import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Nav } from '@/components/Nav';
import { Footer } from '@/components/Footer';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Loader2, Mail, MailOpen, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function Admin() {
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const [tab, setTab] = useState('bookings');
    const [bookings, setBookings] = useState([]);
    const [messages, setMessages] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (authLoading) return;
        if (!user) { navigate('/'); return; }
        if (user.role !== 'admin') { navigate('/mon-compte'); return; }
        load();
    }, [user, authLoading, navigate]);

    const load = async () => {
        setLoading(true);
        try {
            const [b, m, s] = await Promise.all([
                api.get('/bookings/all'),
                api.get('/contact'),
                api.get('/admin/stats'),
            ]);
            setBookings(b.data);
            setMessages(m.data);
            setStats(s.data);
        } catch {
            toast.error('Erreur de chargement');
        } finally { setLoading(false); }
    };

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/bookings/${id}/status`, { status });
            setBookings(bs => bs.map(b => b.booking_id === id ? { ...b, status } : b));
            toast.success('Statut mis à jour');
        } catch {
            toast.error('Erreur');
        }
    };

    const markRead = async (id) => {
        try {
            await api.patch(`/contact/${id}/read`);
            setMessages(ms => ms.map(m => m.message_id === id ? { ...m, is_read: true } : m));
        } catch {}
    };

    if (authLoading || !user || user.role !== 'admin') {
        return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="animate-spin text-[#C5A059]" /></div>;
    }

    return (
        <div className="bg-[#050505] min-h-screen text-[#F8F8F5]">
            <Nav />
            <div className="max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20">
                <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-3">Espace administrateur</div>
                <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight mb-12">Tableau de bord</h1>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#222] mb-12">
                        {[
                            ['Réservations', stats.bookings_total],
                            ['En attente', stats.bookings_pending],
                            ['Confirmées', stats.bookings_confirmed],
                            ['Messages', stats.messages_total],
                            ['Non lus', stats.messages_unread],
                        ].map(([label, val]) => (
                            <div key={label} className="bg-black p-6">
                                <div className="text-xs uppercase tracking-[0.2em] text-[#A3A39D]">{label}</div>
                                <div className="font-display text-3xl text-[#C5A059] mt-2 font-light">{val}</div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Tabs */}
                <div className="flex gap-8 border-b border-[#222] mb-8">
                    <button data-testid="admin-tab-bookings" onClick={() => setTab('bookings')}
                        className={`pb-4 text-xs uppercase tracking-[0.25em] transition-colors ${tab === 'bookings' ? 'text-[#C5A059] border-b-2 border-[#C5A059] -mb-px' : 'text-[#A3A39D] hover:text-[#F8F8F5]'}`}>
                        Réservations ({bookings.length})
                    </button>
                    <button data-testid="admin-tab-messages" onClick={() => setTab('messages')}
                        className={`pb-4 text-xs uppercase tracking-[0.25em] transition-colors ${tab === 'messages' ? 'text-[#C5A059] border-b-2 border-[#C5A059] -mb-px' : 'text-[#A3A39D] hover:text-[#F8F8F5]'}`}>
                        Messages ({messages.length})
                    </button>
                </div>

                {loading ? (
                    <Loader2 className="animate-spin text-[#C5A059]" />
                ) : tab === 'bookings' ? (
                    bookings.length === 0 ? (
                        <p className="text-[#A3A39D]">Aucune réservation pour l'instant.</p>
                    ) : (
                        <div className="space-y-2">
                            {bookings.map(b => (
                                <div key={b.booking_id} data-testid={`admin-booking-${b.booking_id}`}
                                    className="border border-[#222] hover:border-[#C5A059]/40 p-6 grid grid-cols-1 md:grid-cols-6 gap-4 text-sm transition-colors">
                                    <div>
                                        <div className="font-mono text-xs text-[#A3A39D]">{b.booking_id.slice(0, 10).toUpperCase()}</div>
                                        <div className="text-[#F8F8F5] mt-1">{b.contact_name}</div>
                                        <div className="text-xs text-[#A3A39D]">{b.contact_phone}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-[#A3A39D]">{b.pickup_date} {b.pickup_time}</div>
                                        <div className="text-xs text-[#C5A059] uppercase tracking-wider mt-1">{b.vehicle_type}</div>
                                    </div>
                                    <div className="md:col-span-2">
                                        <div className="truncate">{b.pickup_city ? `${b.pickup_city} · ` : ''}{b.pickup_address}</div>
                                        <div className="text-[#A3A39D] truncate">→ {b.dropoff_city ? `${b.dropoff_city} · ` : ''}{b.dropoff_address}</div>
                                        <div className="text-xs text-[#A3A39D] mt-1">{b.service_type} · {b.passengers} pax</div>
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wider text-[#A3A39D]">Prix</div>
                                        <div className="font-display text-xl text-[#C5A059]">{b.estimated_price?.toFixed(2)} €</div>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <select data-testid={`admin-status-${b.booking_id}`} value={b.status} onChange={(e) => updateStatus(b.booking_id, e.target.value)}
                                            className="bg-black border border-[#222] focus:border-[#C5A059] text-[#F8F8F5] px-3 py-2 text-xs uppercase tracking-wider outline-none">
                                            <option value="pending">En attente</option>
                                            <option value="confirmed">Confirmée</option>
                                            <option value="completed">Terminée</option>
                                            <option value="cancelled">Annulée</option>
                                        </select>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                ) : (
                    messages.length === 0 ? (
                        <p className="text-[#A3A39D]">Aucun message.</p>
                    ) : (
                        <div className="space-y-3">
                            {messages.map(m => (
                                <div key={m.message_id} data-testid={`admin-message-${m.message_id}`}
                                    className={`border p-6 transition-colors ${m.is_read ? 'border-[#222]' : 'border-[#C5A059]/40 bg-[#C5A059]/5'}`}>
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3">
                                                {m.is_read ? <MailOpen size={14} className="text-[#A3A39D]" /> : <Mail size={14} className="text-[#C5A059]" />}
                                                <span className="font-medium">{m.name}</span>
                                                <span className="text-[#A3A39D] text-sm">· {m.email}</span>
                                                {m.phone && <span className="text-[#A3A39D] text-sm">· {m.phone}</span>}
                                            </div>
                                            <div className="text-[#C5A059] mt-2 font-medium">{m.subject}</div>
                                            <p className="text-[#A3A39D] mt-3 text-sm leading-relaxed">{m.message}</p>
                                        </div>
                                        {!m.is_read && (
                                            <button data-testid={`admin-mark-read-${m.message_id}`} onClick={() => markRead(m.message_id)}
                                                className="text-xs uppercase tracking-[0.2em] text-[#C5A059] hover:text-[#DCC283]">
                                                Marquer lu
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                )}
            </div>
            <Footer />
        </div>
    );
}
